import { useLayoutEffect, useRef } from 'react';

import { formMeta } from '../data/forms.js';
import type { Form } from '../fixtures/domain.js';
import { Icon } from '../components/Icon.js';
import { FormPreview } from './FormPreview.js';
import styles from './FormList.module.css';

/**
 * The library as a list you can read, in one narrow column.
 *
 * The other way of choosing a form is the composer's picker, which is a wide
 * dialog and puts the names beside a preview pane. That shape does not survive
 * being made narrow: at 460px the two panes leave the preview about 200px to
 * draw an option list in, and the box under a half-filled list of names is dead
 * space with a second scrollbar in it. So this is not that layout squeezed —
 * it is the same job done the way a narrow frame does it.
 *
 * ONE PRESS, and the row that answers "which of these is it" is the row that
 * takes it: pressing a name selects the form and opens its questions in place,
 * closing whichever was open before. There is nothing else to hit, no chevron
 * beside the name doing a second thing, and no state where the thing being read
 * and the thing being taken are different forms.
 */
export function FormList({
  forms,
  selectedId,
  onSelect,
}: {
  forms: Form[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const listRef = useRef<HTMLUListElement>(null);
  const openRef = useRef<HTMLLIElement>(null);

  /**
   * The opened row to the top of the window, so its questions are on screen
   * rather than under the fold that the press just created. Without it, opening
   * the third of four names shows a head, a hairline, and no answer to the
   * question the press was asking.
   *
   * Layout effect, and scrollTop rather than `scrollIntoView`: this has to land
   * in the same frame as the row that grew, and it must move this list and
   * nothing else — `scrollIntoView` walks every scrollable ancestor.
   */
  useLayoutEffect(() => {
    const list = listRef.current;
    const row = openRef.current;
    if (!list || !row) return;
    list.scrollTop = row.offsetTop;
  }, [selectedId]);

  return (
    <ul className={styles.list} ref={listRef}>
      {forms.map((form) => {
        const open = form.id === selectedId;
        return (
          <li
            key={form.id}
            className={open ? styles.rowOpen : styles.row}
            ref={open ? openRef : undefined}
          >
            <button
              type="button"
              className={styles.head}
              aria-expanded={open}
              onClick={() => onSelect(form.id)}
            >
              <span className={styles.name}>{form.name}</span>
              <span className={styles.count}>{formMeta(form)}</span>
              {/* The mark for the one that is going, at the row's right end.
                  The open questions say it too, but they scroll away from their
                  own head on a long form and this does not. */}
              {open && (
                <span className={styles.mark}>
                  <Icon name="check" size={16} />
                </span>
              )}
            </button>
            {open && (
              <div className={styles.body}>
                <FormPreview fields={form.fields} />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
